/**
 * TierBadge — small metallic pill that represents a NearNow subscription tier.
 *
 * Renders as an SVG so the metal gradients scale crisply on Retina/4K.
 * Palettes:
 *   - starter → Bronze (warm copper)
 *   - growth  → Gold (rich yellow-gold)
 *   - pro     → Platinum (cool silver-white)
 *   - elite   → Diamond gradient (rainbow-tinted silver for AutoPilot Growth)
 *
 * Two size presets:
 *   - "sm" (24px)  — inline next to headings, dashboard header
 *   - "md" (40px)  — inside pricing tier cards
 *
 * Usage:
 *   <TierBadge tier="pro" size="md" data-testid="tier-badge-pro" />
 */

const PALETTES = {
  starter: {
    label: "Bronze",
    outer: ["#C77935", "#7B3F13"],
    inner: ["#F4B071", "#9A5322"],
    edge: "#5C2E0C",
    text: "#F4E3CE",
  },
  growth: {
    label: "Gold",
    outer: ["#F0D67A", "#8B6914"],
    inner: ["#FFF1B8", "#C39616"],
    edge: "#5A4408",
    text: "#3A2C05",
  },
  pro: {
    label: "Platinum",
    outer: ["#E6E9EE", "#8892A3"],
    inner: ["#F7F8FA", "#B3BAC7"],
    edge: "#5E6474",
    text: "#0A1628",
  },
  elite: {
    label: "AutoPilot Growth",
    outer: ["#E3F2FF", "#7FA6D9"],
    inner: ["#FFFFFF", "#B5D0F0"],
    edge: "#3C6398",
    text: "#0A1628",
  },
};

const SIZES = { sm: 24, md: 40, lg: 64 };

export default function TierBadge({ tier = "starter", size = "md", className = "", ...rest }) {
  const p = PALETTES[tier] || PALETTES.starter;
  const dim = SIZES[size] || SIZES.md;
  const gid = `tb-${tier}-${size}`;

  return (
    <svg width={dim} height={dim} viewBox="0 0 64 64"
         xmlns="http://www.w3.org/2000/svg"
         className={className}
         data-testid={rest["data-testid"] || `tier-badge-${tier}`}
         aria-label={`${p.label} tier`}
         style={{ filter: "drop-shadow(0 4px 10px rgba(10,22,40,0.25))", flexShrink: 0 }}>
      <defs>
        <radialGradient id={`${gid}-outer`} cx="30%" cy="20%" r="90%">
          <stop offset="0%" stopColor={p.outer[0]} />
          <stop offset="100%" stopColor={p.outer[1]} />
        </radialGradient>
        <radialGradient id={`${gid}-inner`} cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor={p.inner[0]} />
          <stop offset="100%" stopColor={p.inner[1]} />
        </radialGradient>
        {tier === "elite" && (
          <linearGradient id={`${gid}-shine`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFEAF7" stopOpacity="0.9" />
            <stop offset="35%" stopColor="#D8F0FF" stopOpacity="0.8" />
            <stop offset="65%" stopColor="#FFF7D8" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#E6D8FF" stopOpacity="0.6" />
          </linearGradient>
        )}
      </defs>

      {/* Outer ring — brushed metal look */}
      <circle cx="32" cy="32" r="30" fill={`url(#${gid}-outer)`} stroke={p.edge} strokeWidth="1.4" />

      {/* Inner disk */}
      <circle cx="32" cy="32" r="22" fill={`url(#${gid}-inner)`} stroke={p.edge} strokeWidth="0.8" opacity="0.95" />

      {/* Elite gets an iridescent sheen ring on top */}
      {tier === "elite" && (
        <circle cx="32" cy="32" r="22" fill={`url(#${gid}-shine)`} opacity="0.55" />
      )}

      {/* Star / crown flourish per tier */}
      {tier === "starter" && <StarGlyph fill={p.text} />}
      {tier === "growth" && <CrownGlyph fill={p.text} />}
      {tier === "pro" && <ShieldGlyph fill={p.text} />}
      {tier === "elite" && <DiamondGlyph fill={p.text} />}

      {/* Top highlight — the glossy sheen */}
      <ellipse cx="26" cy="16" rx="14" ry="5" fill="#ffffff" opacity="0.32" />
    </svg>
  );
}

const StarGlyph = ({ fill }) => (
  <path fill={fill}
        d="M32 20 L34.6 27.5 L42.5 27.9 L36.2 32.7 L38.4 40.4 L32 35.9 L25.6 40.4 L27.8 32.7 L21.5 27.9 L29.4 27.5 Z" />
);
const CrownGlyph = ({ fill }) => (
  <path fill={fill}
        d="M20 40 L20 26 L26 30 L32 22 L38 30 L44 26 L44 40 Z M22 42 L42 42 L42 44 L22 44 Z" />
);
const ShieldGlyph = ({ fill }) => (
  <path fill={fill}
        d="M32 20 L42 24 L42 34 C42 38.5 37.5 42 32 44 C26.5 42 22 38.5 22 34 L22 24 Z M28 30 L31 33 L37 27" strokeLinejoin="round" strokeLinecap="round" stroke={fill} strokeWidth="0.8" />
);
const DiamondGlyph = ({ fill }) => (
  <g fill={fill}>
    <path d="M32 20 L42 28 L32 44 L22 28 Z" />
    <path d="M22 28 L42 28" stroke={fill} strokeWidth="0.8" fill="none" />
    <path d="M28 22 L32 28 L36 22" stroke={fill} strokeWidth="0.8" fill="none" />
  </g>
);

/** Small pill combining the badge + tier label — used in the dashboard header */
export function TierBadgePill({ tier = "starter", size = "sm", showLabel = true, style = {}, testid }) {
  const p = PALETTES[tier] || PALETTES.starter;
  return (
    <span
      data-testid={testid || `tier-pill-${tier}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "6px 12px 6px 6px", borderRadius: 999,
        background: "linear-gradient(135deg, rgba(10,22,40,0.06), rgba(10,22,40,0.02))",
        border: `1px solid ${p.edge}22`,
        fontWeight: 700, letterSpacing: 1, fontSize: 12,
        color: "#0A1628",
        ...style,
      }}>
      <TierBadge tier={tier} size={size} />
      {showLabel && <span>{p.label.toUpperCase()}</span>}
    </span>
  );
}
